import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Facebook, Globe, Mail, MapPin, MessageCircle, Phone, ShieldCheck } from "lucide-react";

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["ความปลอดภัย", "/privacy"],
];

const AppNav = () => (
  <header className="sticky top-0 z-50 w-full border-b border-[#E3E6DF] bg-[#FAFAF7]/90 backdrop-blur-xl">
    <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8">
      <Link to="/" className="flex items-center gap-3" aria-label="KHORN AI home">
        <span className="relative block h-11 w-[172px] shrink-0 overflow-hidden">
          <img src="/logo_and_name.png" alt="KHORN AI" className="absolute -left-[28px] -top-[60px] h-[168px] max-w-none" />
        </span>
      </Link>

      <nav className="hidden items-center gap-8 lg:flex">
        {navItems.map(([label, href]) => (
          <a key={label} href={href} className="text-xs font-bold uppercase tracking-[0.16em] text-[#6A7168] transition hover:text-[#15130F]">
            {label}
          </a>
        ))}
      </nav>

      <div className="flex items-center gap-3">
        <a href="https://app.khorn-ai.com" className="hidden text-xs font-bold uppercase tracking-[0.16em] text-[#6A7168] transition hover:text-[#15130F] md:block">
          เข้าสู่ระบบ
        </a>
        <a href="https://app.khorn-ai.com/plan" className="rounded-full bg-[#15130F] px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#1F2A22]">
          เริ่มใช้งาน
        </a>
      </div>
    </div>
  </header>
);

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#15130F] selection:bg-[#15130F] selection:text-white">
      <Helmet>
        <title>ติดต่อ KHORN AI | AI สำหรับสำนักงานกฎหมาย</title>
        <meta
          name="description"
          content="ติดต่อ KHORN AI เพื่อสอบถาม AI สำหรับสำนักงานกฎหมาย การรับข้อมูลลูกความ จัดเคส มอบหมายงาน สร้างเอกสาร จัดหลักฐาน และติดตามงานค้าง"
        />
        <meta name="keywords" content="ติดต่อ KHORN AI, ค้อนเอไอ, AI สำนักงานกฎหมาย, AI จัดการเคส, legal AI contact" />
        <meta property="og:title" content="ติดต่อ KHORN AI" />
        <meta property="og:description" content="สอบถามการใช้งาน AI สำหรับสำนักงานกฎหมายและงานลูกความ" />
        <meta property="og:url" content="https://www.khorn-ai.com/contact" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.khorn-ai.com/contact" />
      </Helmet>

      <AppNav />

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute left-1/2 top-0 h-[520px] w-[860px] -translate-x-1/2 rounded-full bg-[#EEF3EA]/70 blur-3xl" />
          <div className="relative mx-auto grid min-h-[calc(100vh-80px)] max-w-7xl gap-12 px-5 py-20 md:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:py-28">
            <section>
              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#E3E6DF] bg-[#FFFFFF] px-4 py-2 text-xs font-bold text-[#6A7168] shadow-sm">
                <ShieldCheck size={14} />
                Contact KHORN AI
              </div>
              <h1 className="text-5xl font-semibold leading-tight tracking-tight md:text-7xl">
                คุยกับทีมเราเรื่อง workflow ของสำนักงานกฎหมาย
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-[#5F665E]">
                ติดต่อทีม KHORN AI เพื่อสอบถามการใช้งาน แพ็กเกจ การรับข้อมูลจาก LINE และอีเมล การจัดเคส การสร้างเอกสาร หรือการวางระบบให้ทีมกฎหมายของคุณ
              </p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <a href="https://app.khorn-ai.com/plan" className="inline-flex h-14 items-center justify-center gap-3 rounded-full bg-[#15130F] px-8 text-sm font-bold text-white transition hover:bg-[#1F2A22]">
                  เริ่มใช้งาน
                  <ArrowRight size={16} />
                </a>
                <a href="https://lin.ee/VRC467G" target="_blank" rel="noopener noreferrer" className="inline-flex h-14 items-center justify-center rounded-full border border-[#D8DDD4] px-8 text-sm font-bold text-[#15130F] transition hover:border-[#8FA083]">
                  คุยผ่าน LINE
                </a>
              </div>
            </section>

            <section className="grid gap-5">
              <div className="grid gap-5 md:grid-cols-2">
                <a href="https://lin.ee/VRC467G" target="_blank" rel="noopener noreferrer" className="rounded-[30px] border border-[#8FA083]/20 bg-[#FFFFFF] p-8 transition hover:border-[#8FA083]/60 hover:bg-[#FFFFFF]">
                  <MessageCircle size={30} className="mb-8 text-[#6A7168]" />
                  <h2 className="text-2xl font-semibold">บัญชี LINE ทางการ</h2>
                  <p className="mt-4 text-sm leading-relaxed text-[#5F665E]">แชทกับทีมงานเพื่อถามเรื่องการใช้งาน แพ็กเกจ หรือการใช้งานจริงในสำนักงาน</p>
                </a>
                <a href="https://www.facebook.com/khornaisolution" target="_blank" rel="noopener noreferrer" className="rounded-[30px] border border-[#E3E6DF] bg-[#FFFFFF] p-8 shadow-sm transition hover:border-[#8FA083]/60">
                  <Facebook size={30} className="mb-8 text-[#15130F]" />
                  <h2 className="text-2xl font-semibold">เพจ Facebook ทางการ</h2>
                  <p className="mt-4 text-sm leading-relaxed text-[#5F665E]">ติดตามเพจทางการ ข่าวสาร และตัวอย่าง workflow จาก KHORN AI</p>
                </a>
              </div>

              <div className="rounded-[34px] border border-[#E3E6DF] bg-[#FFFFFF] p-8 shadow-[0_24px_80px_rgba(0,0,0,0.08)]">
                <div className="grid gap-8 md:grid-cols-3">
                  {[
                    { icon: Phone, label: "โทรติดต่อ", value: "081-811-8772", href: "tel:081-811-8772" },
                    { icon: Mail, label: "อีเมลติดต่อ", value: "contact@khorn-ai.com", href: "mailto:contact@khorn-ai.com" },
                    { icon: Globe, label: "เว็บไซต์", value: "www.khorn-ai.com", href: "/" },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <a key={item.label} href={item.href} className="group">
                        <Icon size={20} className="mb-4 text-[#6A7168]" />
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6A7168]">{item.label}</div>
                        <div className="mt-2 text-sm font-semibold text-[#15130F] group-hover:text-[#6A7168]">{item.value}</div>
                      </a>
                    );
                  })}
                </div>

                <div className="mt-8 border-t border-[#E3E6DF] pt-8">
                  <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6A7168]">
                    <MapPin size={14} />
                    Address
                  </div>
                  <p className="text-sm font-semibold text-[#15130F]">ค้อนเอไอ โซลูชั่น จำกัด</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#5F665E]">
                    54 ซอย 37 ถนนเฉลิมพระเกียรติร.9 หนองบอน ประเวศ กรุงเทพฯ 10250
                  </p>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>
    </div>
  );
}
